import { Controller } from "@hotwired/stimulus"
import { confirmNewCompany } from "../lib/company_duplicate"

// Settings → General → Owner Reps (James / Phelipe 2026-10-20): the switch
// saves itself; picking a company in the search (or registering a new one)
// submits the add form.
export default class extends Controller {
  static targets = ["addForm", "companyId", "companyName", "confirm", "confirmModal", "confirmTitle", "confirmList",
                    "revertModal", "revertText", "revertForm", "historyModal",
                    "changesModal", "changesTitle", "changesBody", "removeModal", "removeTitle", "removeText", "removeForm", "removeSummary"]
  static values = { previewUrl: String }

  toggle(event) {
    event.target.form?.requestSubmit()
  }

  // An existing company: first ask the server where its role carries money
  // (pay requests); if anywhere, the confirm pop-up lists those projects.
  async pick(event) {
    const d = event.detail || {}
    const id = d.id ?? d.value ?? d.company_id
    if (!id) return
    this.companyIdTarget.value = id
    this.companyNameTarget.value = ""
    this.confirmTarget.value = ""
    let preview = null
    try {
      const resp = await fetch(`${this.previewUrlValue}?company_id=${encodeURIComponent(id)}`, { headers: { Accept: "application/json" } })
      if (resp.ok) preview = await resp.json()
    } catch { /* add as usual */ }
    if (preview && (preview.projects || []).length) {
      this._showConfirm(preview)
      return
    }
    this.addFormTarget.requestSubmit()
  }

  confirmAdd() {
    this.confirmTarget.value = "1"
    this.closeConfirm()
    this.addFormTarget.requestSubmit()
  }

  // History → "N projects": what that step changed, project by project.
  openChanges(event) {
    const btn = event.currentTarget
    const tpl = btn.nextElementSibling
    this.changesTitleTarget.textContent = btn.dataset.title || ""
    this.changesBodyTarget.innerHTML = tpl && tpl.tagName === "TEMPLATE" ? tpl.innerHTML : ""
    this.changesModalTarget.classList.remove("hidden")
  }

  closeChanges() { this.changesModalTarget.classList.add("hidden") }

  // × on a company: a styled confirm before it is taken off the list.
  openRemove(event) {
    const btn = event.currentTarget
    const d = btn.dataset
    const tpl = btn.nextElementSibling
    this.removeFormTarget.action = d.url
    this.removeTitleTarget.textContent = `Remove ${d.company} as Owner's Rep?`
    this.removeTextTarget.textContent = "This is what goes back. Its people stay on every project, and you can undo it from History."
    this.removeSummaryTarget.innerHTML = tpl && tpl.tagName === "TEMPLATE" ? tpl.innerHTML : ""
    this.removeModalTarget.classList.remove("hidden")
  }

  closeRemove() { this.removeModalTarget.classList.add("hidden") }

  openHistory() { this.historyModalTarget.classList.remove("hidden") }
  closeHistory() { this.historyModalTarget.classList.add("hidden") }

  // History → Revert: the same styled confirm, then the form posts.
  openRevert(event) {
    const d = event.currentTarget.dataset
    this.revertFormTarget.action = d.url
    const n = Number(d.projects || 0)
    this.revertTextTarget.textContent = `"${d.label}" will be undone${n ? `, with what it changed on ${n} project${n === 1 ? "" : "s"}` : ""}: the listing, the roles and any Past PRs go back to how they were before. Anything changed by hand since stays as it is.`
    this.revertModalTarget.classList.remove("hidden")
  }

  closeRevert() { this.revertModalTarget.classList.add("hidden") }

  closeConfirm() { this.confirmModalTarget.classList.add("hidden") }
  stopPropagation(event) { event.stopPropagation() }

  _showConfirm(p) {
    const n = p.projects.length
    this.confirmTitleTarget.textContent = `${p.company_name} has pay requests on ${n} of your project${n === 1 ? "" : "s"}`
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    this.confirmListTarget.innerHTML = p.projects.map(pr => `
      <div class="flex items-center justify-between gap-3 py-1 border-b border-gray-100">
        <span class="min-w-0 truncate font-medium text-gray-900">${esc(pr.number ? `${pr.number} - ${pr.name}` : pr.name)}</span>
        <span class="shrink-0 px-2 py-0.5 rounded-full text-[13px] font-medium bg-amber-100 text-amber-700">${esc(pr.role)}</span>
      </div>`).join("")
    this.confirmModalTarget.classList.remove("hidden")
  }

  // "Already in ConsLog?" (Kloepfer K8-6, Phelipe 2026-10-27): an existing
  // company goes through the pick path (its money check included); only a
  // truly new one is registered.
  async register(event) {
    const query = (event.detail?.query || "").trim()
    if (!query) return
    const { choice, company } = await confirmNewCompany(query, { screen: "owner_reps" })
    if (choice === "cancel") return
    if (choice === "existing") return this.pick({ detail: { id: company.id } })
    this.companyIdTarget.value = ""
    this.companyNameTarget.value = query
    this.confirmTarget.value = ""
    this.addFormTarget.requestSubmit()
  }

  // A company row's email domains: saved as soon as the box changes; the
  // server sends back the tidy form ("@jll.com, @jll.test").
  async saveDomains(event) {
    const input = event.target
    try {
      const resp = await fetch(input.dataset.url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ email_domains: input.value })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't save the domains.")
      input.value = data.email_domains_text || ""
      this._toast(data.message || "Saved.", "notice")
    } catch (err) {
      this._toast(err.message || "Couldn't save the domains.", "alert")
    }
  }

  _toast(message, type) {
    const el = document.createElement("div")
    el.className = `flash-toast-${type === "alert" ? "alert" : "notice"}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
